import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Mail, Sparkles, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  Easing,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FadeIn } from '../src/components/animations/FadeIn';
import { BrandIcon } from '../src/components/landing/BrandIcon';
import { FormInput } from '../src/components/primitives/FormInput';
import { Text } from '../src/components/primitives/Text';
import { useAuth } from '../src/contexts/AuthContext';
import { type TranslationParams, useTranslation } from '../src/contexts/TranslationContext';
import { OtpInput } from '../src/features/auth/components/OtpInput';
import { safeReturnPath } from '../src/features/auth/returnTo';
import { useReducedMotion } from '../src/hooks/useReducedMotion';
import { useResponsive } from '../src/hooks/useResponsive';
import { colors } from '../src/theme/colors';
import { tokens } from '../src/theme/tokens';
import { darkPrecisionFontFamilies, fieldPlaceholderColor } from '../src/theme/typography';

// Forward (input -> code) slides the incoming phase in from the right; going
// back (code -> input) slides it from the left. Error reuses the input rank so
// it reads as a sibling of the email step, not a "forward" progression.
const PHASE_RANK: Record<string, number> = { input: 0, error: 0, code: 1 };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COOLDOWN_MS = 30_000;
// Persisted so returning users skip the mobile intro on subsequent visits.
const LOGIN_INTRO_SEEN_KEY = 'login_intro_seen';

function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

function secondsRemaining(lastSentAt: number): number {
  return Math.max(0, Math.ceil((COOLDOWN_MS - (Date.now() - lastSentAt)) / 1000));
}

// ---------------------------------------------------------------------------
// Idle / requesting view — email input + primary CTA
// ---------------------------------------------------------------------------

interface InputViewProps {
  email: string;
  onChangeEmail: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  // Set to the email that was just rejected with ORG_DOMAIN_EXISTS. While the
  // entered email still matches it, sending another link is futile, so the CTA
  // stays disabled and an inline note explains why (user must change the email).
  blockedEmail?: string | null;
  t: (key: string, params?: TranslationParams) => string;
}

function InputView({
  email,
  onChangeEmail,
  onSubmit,
  submitting,
  blockedEmail,
  t,
}: InputViewProps) {
  const isBlocked = !!blockedEmail && email.trim().toLowerCase() === blockedEmail;
  const disabled = !isValidEmail(email) || submitting || isBlocked;
  const [focused, setFocused] = useState(false);

  return (
    <View style={styles.form}>
      <Text nativeID="magic-link-email-label" style={styles.label} accessibilityRole="none">
        {t('common.sources.email')}
      </Text>
      <View style={[styles.inputField, focused && styles.inputFieldFocused]}>
        <Mail size={18} color={colors.text.tertiary} style={styles.inputIcon} />
        <FormInput
          testID="magic-link-email-input"
          accessibilityLabel={t('common.sources.email')}
          accessibilityLabelledBy="magic-link-email-label"
          autoComplete="email"
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          value={email}
          onChangeText={onChangeEmail}
          onSubmitEditing={disabled ? undefined : onSubmit}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={styles.inputControl}
          placeholderTextColor={fieldPlaceholderColor}
          placeholder="you@example.com"
          editable={!submitting}
        />
        {email.length > 0 && !submitting ? (
          <Pressable
            testID="magic-link-email-clear"
            onPress={() => onChangeEmail('')}
            accessibilityRole="button"
            accessibilityLabel={t('common.actions.clear')}
            style={styles.clearButton}
          >
            <X size={14} color={colors.text.tertiary} />
          </Pressable>
        ) : null}
      </View>
      {isBlocked ? (
        <Text
          testID="magic-link-blocked-note"
          style={styles.blockedNote}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          {t('auth.orgDomainExistsInline')}
        </Text>
      ) : (
        <Text style={styles.hint}>{t('auth.magicLinkHint')}</Text>
      )}
      <Pressable
        testID="magic-link-send-cta"
        onPress={disabled ? undefined : onSubmit}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        style={[styles.cta, disabled && styles.ctaDisabled]}
      >
        {submitting ? (
          <ActivityIndicator size="small" color={colors.background.primary} />
        ) : (
          <Text style={[styles.ctaText, disabled && styles.ctaTextDisabled]}>
            {t('auth.sendMagicLink')}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Code-entry view — masked email + 6-digit OTP input + verify + countdown
// ---------------------------------------------------------------------------

interface CodeEntryViewProps {
  maskedEmail: string;
  lastSentAt: number;
  verifying: boolean;
  // FLOSEAI-439: true while a resend is in flight; true once it has just landed.
  resending?: boolean;
  justResent?: boolean;
  onSubmitCode: (code: string) => void;
  onResend: () => void;
  onDifferentEmail: () => void;
  t: (key: string, params?: TranslationParams) => string;
}

export function CodeEntryView({
  maskedEmail,
  lastSentAt,
  verifying,
  resending = false,
  justResent = false,
  onSubmitCode,
  onResend,
  onDifferentEmail,
  t,
}: CodeEntryViewProps) {
  const [code, setCode] = useState('');
  const [secs, setSecs] = useState(() => secondsRemaining(lastSentAt));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Announce the inbox handoff once when this phase appears — the slide
  // transition is silent to screen readers otherwise (SC 4.1.3).
  useEffect(() => {
    AccessibilityInfo.announceForAccessibility(`${t('auth.checkInbox')}. ${maskedEmail}`);
    // Mount-only: re-announcing on every re-render would spam the SR.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tick = () => {
      const remaining = secondsRemaining(lastSentAt);
      setSecs(remaining);
      if (remaining === 0 && intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [lastSentAt]);

  const resendDisabled = secs > 0;
  const verifyDisabled = verifying || code.trim().length < 6;

  return (
    <View testID="magic-link-interstitial" style={styles.form} accessibilityLiveRegion="polite">
      <Text style={styles.interstitialTitle}>{t('auth.checkInbox')}</Text>
      <Text
        testID="magic-link-masked-email"
        style={styles.maskedEmail}
        accessibilityLabel={`${t('auth.checkInbox')} — ${maskedEmail}`}
      >
        {maskedEmail}
      </Text>
      {justResent ? (
        <Text
          testID="magic-link-resent-confirmation"
          style={styles.resentConfirmation}
          accessibilityLiveRegion="polite"
        >
          {t('auth.codeResent', { email: maskedEmail })}
        </Text>
      ) : null}
      <Text nativeID="magic-link-code-label" style={styles.label}>
        {t('auth.enterCode')}
      </Text>

      <OtpInput
        testID="magic-link-code-input"
        accessibilityLabelledBy="magic-link-code-label"
        accessibilityLabel={t('auth.enterCode')}
        autoFocus
        value={code}
        onChange={setCode}
        editable={!verifying}
      />

      <Pressable
        testID="magic-link-verify-cta"
        onPress={verifyDisabled ? undefined : () => onSubmitCode(code.trim())}
        disabled={verifyDisabled}
        accessibilityRole="button"
        accessibilityState={{ disabled: verifyDisabled }}
        style={[styles.cta, verifyDisabled && styles.ctaDisabled]}
      >
        {verifying ? (
          <ActivityIndicator size="small" color={colors.background.primary} />
        ) : (
          <Text style={[styles.ctaText, verifyDisabled && styles.ctaTextDisabled]}>
            {t('auth.verifyCode')}
          </Text>
        )}
      </Pressable>

      <Text style={styles.hint}>{t('auth.checkSpam')}</Text>

      <Pressable
        testID="magic-link-resend-cta"
        onPress={resendDisabled || verifying || resending ? undefined : onResend}
        disabled={resendDisabled || verifying || resending}
        accessibilityRole="button"
        accessibilityState={{ disabled: resendDisabled || verifying || resending }}
        style={styles.ctaSecondary}
      >
        {resending ? (
          <ActivityIndicator size="small" color={colors.text.secondary} />
        ) : (
          <Text
            style={[
              styles.ctaSecondaryText,
              (resendDisabled || verifying) && styles.ctaSecondaryTextDisabled,
            ]}
          >
            {resendDisabled ? t('auth.resendIn', { seconds: secs }) : t('auth.sendMagicLink')}
          </Text>
        )}
      </Pressable>

      <Pressable
        testID="magic-link-different-email"
        onPress={verifying ? undefined : onDifferentEmail}
        disabled={verifying}
        accessibilityRole="button"
        style={styles.differentEmail}
      >
        <Text style={styles.differentEmailText}>{t('auth.differentEmail')}</Text>
      </Pressable>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Error view
// ---------------------------------------------------------------------------

interface ErrorViewProps {
  errorCode: string;
  onResend: () => void;
  // org_domain_exists only: context for the message + a way back to the email step.
  organizationName?: string;
  domain?: string;
  onDifferentEmail?: () => void;
  onJoinWaitlist?: () => void;
  t: (key: string, params?: TranslationParams) => string;
}

function errorCopyKey(code: string): string {
  switch (code) {
    case 'link_invalid':
    case 'invalid_email':
      return 'auth.linkInvalid';
    case 'rate_limited':
      return 'auth.rateLimited';
    // Auth0 connection unavailable — we can't send a link (REQ-AUTH-ML-001).
    case 'connection_disabled':
      return 'auth.connectionUnavailable';
    // The user's account is disabled (REQ-AUTH-ML-011) — distinct from above.
    case 'account_disabled':
      return 'auth.accountDisabled';
    // Email already linked to another account (REQ-AUTH-ML-013).
    case 'email_taken':
      return 'auth.emailTaken';
    // The OTP code the user typed was wrong/expired (D-SEND-2).
    case 'invalid_code':
      return 'auth.codeInvalid';
    // An org already exists for this email's company domain (ORG_DOMAIN_EXISTS).
    case 'org_domain_exists':
      return 'auth.orgDomainExists';
    default:
      return 'auth.unknownError';
  }
}

function ErrorView({
  errorCode,
  onResend,
  organizationName,
  domain,
  onDifferentEmail,
  onJoinWaitlist,
  t,
}: ErrorViewProps) {
  const showResend =
    errorCode === 'link_invalid' ||
    errorCode === 'invalid_email' ||
    errorCode === 'connection_disabled' ||
    errorCode === 'unknown';

  // Free capacity is full: retrying cannot succeed, so point to the waitlist.
  if (errorCode === 'free_capacity_exhausted') {
    return (
      <View style={styles.form}>
        <Text testID="magic-link-error-title" style={styles.errorTitle} accessibilityRole="header">
          {t('auth.freeCapacityExhaustedTitle')}
        </Text>
        <Text
          testID="magic-link-error-message"
          style={styles.errorBody}
          accessibilityRole="alert"
          accessibilityLiveRegion="assertive"
        >
          {t('auth.freeCapacityExhausted')}
        </Text>
        {onJoinWaitlist && (
          <Pressable
            testID="magic-link-error-join-waitlist"
            onPress={onJoinWaitlist}
            accessibilityRole="button"
            style={styles.cta}
          >
            <Text style={styles.ctaText}>{t('auth.joinWaitlist')}</Text>
          </Pressable>
        )}
      </View>
    );
  }

  // Company-domain block: a richer, non-alarming explanation with the org named,
  // plus a route back to the email step (retrying the same address won't help).
  if (errorCode === 'org_domain_exists') {
    const orgName = organizationName || t('auth.orgDomainExistsOrgFallback');
    const body = t('auth.orgDomainExists', { organizationName: orgName, domain: domain ?? '' });

    return (
      <View style={styles.form}>
        <Text testID="magic-link-error-title" style={styles.errorTitle} accessibilityRole="header">
          {t('auth.orgDomainExistsTitle')}
        </Text>
        <Text
          testID="magic-link-error-message"
          style={styles.errorBody}
          accessibilityRole="alert"
          accessibilityLiveRegion="assertive"
        >
          {body}
        </Text>
        {onDifferentEmail && (
          <Pressable
            testID="magic-link-error-different-email"
            onPress={onDifferentEmail}
            accessibilityRole="button"
            style={styles.ctaSecondary}
          >
            <Text style={styles.ctaSecondaryText}>{t('auth.differentEmail')}</Text>
          </Pressable>
        )}
      </View>
    );
  }

  return (
    <View style={styles.form}>
      <Text
        testID="magic-link-error-message"
        style={styles.errorText}
        accessibilityRole="alert"
        accessibilityLiveRegion="assertive"
      >
        {t(errorCopyKey(errorCode))}
      </Text>

      {showResend && (
        <Pressable
          testID="magic-link-error-resend-cta"
          onPress={onResend}
          accessibilityRole="button"
          style={styles.cta}
        >
          <Text style={styles.ctaText}>{t('auth.sendMagicLink')}</Text>
        </Pressable>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// PhaseSlide — animates the incoming phase in (slide + fade) on phase change
// ---------------------------------------------------------------------------

interface PhaseSlideProps {
  phaseKey: string;
  children: React.ReactNode;
}

function PhaseSlide({ phaseKey, children }: PhaseSlideProps) {
  const reducedMotion = useReducedMotion();
  const translateX = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;
  const prevKey = useRef(phaseKey);

  useEffect(() => {
    if (prevKey.current === phaseKey) {
      return;
    }
    const direction = PHASE_RANK[phaseKey] >= PHASE_RANK[prevKey.current] ? 1 : -1;
    prevKey.current = phaseKey;

    if (reducedMotion) {
      translateX.setValue(0);
      opacity.setValue(1);
      return;
    }

    translateX.setValue(28 * direction);
    opacity.setValue(0);
    Animated.parallel([
      Animated.timing(translateX, {
        toValue: 0,
        duration: tokens.duration.normal,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: tokens.duration.normal,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [phaseKey, reducedMotion, translateX, opacity]);

  return (
    <Animated.View testID="login-phase" style={{ opacity, transform: [{ translateX }] }}>
      {children}
    </Animated.View>
  );
}

// ---------------------------------------------------------------------------
// Brand — full company panel (desktop) and compact header (mobile)
// ---------------------------------------------------------------------------

function BrandPanel({ t }: { t: (key: string, params?: TranslationParams) => string }) {
  return (
    <View testID="login-brand-header" style={styles.brandPanel}>
      <LinearGradient
        colors={[colors.background.gradient.middle, colors.background.gradient.end]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.brandPanelInner}>
        <View style={styles.brandPanelHeader}>
          <BrandIcon icon={Sparkles} size="md" />
          <Text style={styles.brandWordmark}>{t('auth.title')}</Text>
        </View>

        <View style={styles.brandPanelCenter}>
          <Text style={styles.panelHeadline}>{t('auth.panelHeadline')}</Text>
          <Text style={styles.panelSubhead}>{t('auth.panelSubhead')}</Text>
        </View>

        <View style={styles.brandPanelBottom}>
          <View style={styles.socialProofRow}>
            <View style={styles.socialProofDot} />
            <Text style={styles.socialProof}>{t('auth.panelSocialProof')}</Text>
          </View>
          <Text style={styles.brandFooter}>{t('auth.footer')}</Text>
        </View>
      </View>
    </View>
  );
}

function CompactBrandHeader({ t }: { t: (key: string, params?: TranslationParams) => string }) {
  return (
    <View testID="login-brand-header" style={styles.brandHeader}>
      <BrandIcon icon={Sparkles} size="md" style={styles.compactMark} />
      <Text style={[styles.brandTitle, styles.centerText]}>{t('auth.title')}</Text>
      <Text style={[styles.brandSubtitle, styles.centerText]}>{t('auth.subtitle')}</Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// MobileIntro — full-screen trust panel gating the form on phones (iOS/Android).
// Mirrors the desktop company panel; "Get started" advances to the email step.
// ---------------------------------------------------------------------------

function MobileIntro({
  t,
  onGetStarted,
}: {
  t: (key: string, params?: TranslationParams) => string;
  onGetStarted: () => void;
}) {
  const insets = useSafeAreaInsets();

  return (
    <View testID="login-mobile-intro" style={styles.introContainer}>
      <LinearGradient
        colors={[colors.background.gradient.middle, colors.background.gradient.end]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <FadeIn
        style={StyleSheet.flatten([
          styles.introInner,
          {
            paddingTop: insets.top + tokens.spacing['2xl'],
            paddingBottom: insets.bottom + tokens.spacing.xl,
          },
        ])}
      >
        <View style={styles.brandPanelHeader}>
          <BrandIcon icon={Sparkles} size="md" />
          <Text style={styles.brandWordmark}>{t('auth.title')}</Text>
        </View>

        <View style={styles.introCenter}>
          <Text style={styles.panelHeadline}>{t('auth.panelHeadline')}</Text>
          <Text style={styles.panelSubhead}>{t('auth.panelSubhead')}</Text>
        </View>

        <View style={styles.introBottom}>
          <Pressable
            testID="login-get-started"
            onPress={onGetStarted}
            accessibilityRole="button"
            style={styles.cta}
          >
            <Text style={styles.ctaText}>{t('auth.getStarted')}</Text>
          </Pressable>
          <View style={styles.socialProofRow}>
            <View style={styles.socialProofDot} />
            <Text style={styles.socialProof}>{t('auth.panelSocialProof')}</Text>
          </View>
          <Text style={styles.brandFooter}>{t('auth.footer')}</Text>
        </View>
      </FadeIn>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function LoginScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ invite?: string; returnTo?: string }>();
  const {
    requestMagicLink,
    submitMagicLinkCode,
    magicLinkState,
    resetMagicLinkState,
    isAuthenticated,
    isLoading,
  } = useAuth();
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [email, setEmail] = useState('');
  // The email rejected with ORG_DOMAIN_EXISTS. Persists across the error → input
  // transition (independent of magicLinkState) so the send CTA stays disabled
  // while that exact address is still entered.
  const [blockedEmail, setBlockedEmail] = useState<string | null>(null);
  // Mobile-only: gate the form behind a "Get started" intro. Invited users
  // (deep link) skip straight to the form. `started` is hydrated from storage
  // so returning users skip the intro; we hold rendering until then to avoid
  // a flash of the intro for users who've already seen it.
  const [started, setStarted] = useState(false);
  const [introHydrated, setIntroHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    AsyncStorage.getItem(LOGIN_INTRO_SEEN_KEY)
      .then((value) => {
        if (active && value === 'true') {
          setStarted(true);
        }
      })
      .finally(() => {
        if (active) {
          setIntroHydrated(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const markStarted = () => {
    setStarted(true);
    void AsyncStorage.setItem(LOGIN_INTRO_SEEN_KEY, 'true');
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      router.replace(safeReturnPath(params.returnTo) ?? '/dashboard');
    }
  }, [isAuthenticated, isLoading, router, params.returnTo]);

  // Remember the address that hit the company-domain block so the send CTA stays
  // disabled for it even after the user returns to the email step.
  useEffect(() => {
    if (magicLinkState.kind === 'error' && magicLinkState.error_code === 'org_domain_exists') {
      setBlockedEmail(email.trim().toLowerCase());
    }
  }, [magicLinkState, email]);

  const handleSend = async () => {
    await requestMagicLink(email.trim(), { inviteToken: params.invite });
  };

  const handleResend = async () => {
    await requestMagicLink(email.trim(), { inviteToken: params.invite });
  };

  const handleDifferentEmail = () => {
    resetMagicLinkState();
    setEmail('');
  };

  // From the domain-block error: go back to the email step but KEEP the address
  // so it reads as blocked (disabled CTA + note) until the user edits it.
  const handleBlockedDifferentEmail = () => {
    resetMagicLinkState();
  };

  const handleJoinWaitlist = () => {
    resetMagicLinkState();
    router.push('/waitlist');
  };

  const submitting = magicLinkState.kind === 'requesting';

  // Snapshot of the last 'sent' state so the code-entry form stays mounted
  // while magicLinkState transitions to 'verifying' (which carries no email).
  const sentSnapshot = useRef<{ maskedEmail: string; lastSentAt: number } | null>(null);
  if (magicLinkState.kind === 'sent') {
    sentSnapshot.current = {
      maskedEmail: magicLinkState.maskedEmail,
      lastSentAt: magicLinkState.lastSentAt,
    };
  }

  const showCodeEntry = magicLinkState.kind === 'sent' || magicLinkState.kind === 'verifying';
  const phaseKey = showCodeEntry ? 'code' : magicLinkState.kind === 'error' ? 'error' : 'input';

  const card = (
    <View testID="login-card" style={styles.card}>
      <PhaseSlide phaseKey={phaseKey}>
        {magicLinkState.kind === 'idle' || magicLinkState.kind === 'requesting' ? (
          <InputView
            email={email}
            onChangeEmail={setEmail}
            onSubmit={handleSend}
            submitting={submitting}
            blockedEmail={blockedEmail}
            t={t}
          />
        ) : showCodeEntry ? (
          <CodeEntryView
            maskedEmail={sentSnapshot.current?.maskedEmail ?? ''}
            lastSentAt={sentSnapshot.current?.lastSentAt ?? Date.now()}
            verifying={magicLinkState.kind === 'verifying'}
            resending={magicLinkState.kind === 'sent' && !!magicLinkState.resending}
            justResent={magicLinkState.kind === 'sent' && !!magicLinkState.justResent}
            onSubmitCode={(code) => {
              void submitMagicLinkCode(code);
            }}
            onResend={handleResend}
            onDifferentEmail={handleDifferentEmail}
            t={t}
          />
        ) : (
          <ErrorView
            errorCode={magicLinkState.error_code ?? 'unknown'}
            onResend={handleSend}
            organizationName={
              magicLinkState.kind === 'error' ? magicLinkState.organizationName : undefined
            }
            domain={magicLinkState.kind === 'error' ? magicLinkState.domain : undefined}
            onDifferentEmail={handleBlockedDifferentEmail}
            onJoinWaitlist={handleJoinWaitlist}
            t={t}
          />
        )}
      </PhaseSlide>
    </View>
  );

  if (isDesktop) {
    return (
      <View style={styles.splitContainer}>
        <FadeIn style={styles.splitLeft} translateY={0}>
          <BrandPanel t={t} />
        </FadeIn>
        <KeyboardAvoidingView
          style={styles.splitRight}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <LinearGradient
            colors={[colors.background.primary, colors.background.secondary]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <ScrollView
            contentContainerStyle={styles.splitRightScroll}
            keyboardShouldPersistTaps="handled"
          >
            <FadeIn delay={120} style={styles.splitCardSlot}>
              {card}
            </FadeIn>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    );
  }

  const introGateActive = !started && !params.invite && magicLinkState.kind === 'idle';
  // Hold on a blank canvas until storage hydrates so returning users never
  // flash the intro before we know they've seen it.
  if (introGateActive && !introHydrated) {
    return <View style={styles.container} />;
  }
  if (introGateActive) {
    return (
      <View style={styles.container}>
        <MobileIntro t={t} onGetStarted={markStarted} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.shell}>
          <FadeIn>
            <CompactBrandHeader t={t} />
          </FadeIn>
          <FadeIn delay={120}>{card}</FadeIn>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.xl,
  },
  shell: {
    width: '100%',
    maxWidth: 420,
    gap: tokens.spacing.xl,
  },

  // Desktop split layout
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background.primary,
  },
  splitLeft: {
    flex: 1,
    maxWidth: 560,
  },
  splitRight: {
    flex: 1,
  },
  splitRightScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing['3xl'],
  },
  splitCardSlot: {
    width: '100%',
    maxWidth: 420,
  },

  // Company panel (desktop left)
  brandPanel: {
    flex: 1,
    overflow: 'hidden',
    borderRightWidth: 1,
    borderRightColor: colors.border.muted,
  },
  brandPanelInner: {
    flex: 1,
    justifyContent: 'space-between',
    padding: tokens.spacing['3xl'],
    gap: tokens.spacing['2xl'],
  },
  brandPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  brandWordmark: {
    color: colors.text.primary,
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.fontSize.lg * 1.25,
    fontWeight: '700',
    letterSpacing: -0.4,
  },
  brandPanelCenter: {
    flex: 1,
    justifyContent: 'center',
    gap: tokens.spacing.lg,
    maxWidth: 440,
  },
  panelHeadline: {
    color: colors.text.primary,
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: tokens.fontSize['4xl'],
    fontWeight: '800',
    letterSpacing: -1.2,
    lineHeight: tokens.fontSize['4xl'] * 1.2,
  },
  panelSubhead: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.fontSize.lg * 1.5,
  },
  brandPanelBottom: {
    gap: tokens.spacing.lg,
  },
  socialProofRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  socialProofDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.status.success.main,
  },
  socialProof: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.base,
    fontWeight: '500',
  },
  brandFooter: {
    color: colors.text.tertiary,
    fontSize: tokens.fontSize.sm,
  },

  // Mobile intro (full-screen gate)
  introContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  introInner: {
    flex: 1,
    paddingHorizontal: tokens.spacing.xl,
    justifyContent: 'space-between',
    gap: tokens.spacing.xl,
  },
  introCenter: {
    flex: 1,
    justifyContent: 'center',
    gap: tokens.spacing.lg,
  },
  introBottom: {
    gap: tokens.spacing.lg,
  },

  // Brand header (mobile / shared)
  brandHeader: {
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  compactMark: {
    marginBottom: tokens.spacing.xs,
  },
  brandTitle: {
    color: colors.text.primary,
    fontSize: tokens.fontSize.xl,
    fontWeight: '700',
  },
  brandSubtitle: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.base,
  },
  centerText: {
    textAlign: 'center',
  },
  card: {
    backgroundColor: colors.background.card,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.glassCard,
    padding: tokens.spacing.xl,
    ...tokens.shadow?.md,
  },
  form: {
    gap: tokens.spacing.lg,
  },
  label: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.base,
  },
  inputField: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    backgroundColor: colors.background.input,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.lg,
  },
  inputFieldFocused: {
    borderColor: colors.border.focus,
  },
  inputIcon: {
    marginRight: tokens.spacing.md,
  },
  clearButton: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.sm,
    backgroundColor: colors.interactive.hoverSubtle,
    marginLeft: tokens.spacing.sm,
  },
  inputControl: {
    flex: 1,
    paddingVertical: tokens.spacing.md,
    color: colors.foreground,
    fontSize: tokens.fontSize.md,
  },
  cta: {
    backgroundColor: colors.primary.main,
    borderRadius: tokens.radius.md,
    paddingVertical: tokens.spacing.md,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // A muted fill that clearly reads as inactive — replaces a blanket opacity
  // that left the button looking enabled and failing contrast.
  ctaDisabled: {
    backgroundColor: colors.surface.rowHover,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  ctaText: {
    color: colors.background.primary,
    fontWeight: '700',
    fontSize: tokens.fontSize.md,
  },
  ctaTextDisabled: {
    color: colors.text.disabled,
  },
  // Resend is a fallback, not the primary action — quiet ghost button so it
  // never out-shouts "Verify code".
  ctaSecondary: {
    borderRadius: tokens.radius.md,
    paddingVertical: tokens.spacing.md,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: 'transparent',
  },
  ctaSecondaryText: {
    color: colors.text.secondary,
    fontWeight: '600',
    fontSize: tokens.fontSize.md,
  },
  ctaSecondaryTextDisabled: {
    color: colors.text.tertiary,
  },
  hint: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.fontSize.sm * 1.4,
  },
  resentConfirmation: {
    color: colors.status.success.main,
    fontSize: tokens.fontSize.sm,
    fontWeight: '600',
    textAlign: 'center',
  },
  interstitialTitle: {
    color: colors.foreground,
    fontSize: tokens.fontSize.xlPlus,
    fontWeight: '700',
    textAlign: 'center',
  },
  maskedEmail: {
    color: colors.primary.main,
    fontSize: tokens.fontSize.md,
    textAlign: 'center',
  },
  differentEmail: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    padding: tokens.spacing.sm,
  },
  differentEmailText: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.base,
    textDecorationLine: 'underline',
  },
  errorText: {
    color: colors.status.error.light,
    fontSize: tokens.fontSize.basePlus,
    textAlign: 'center',
  },
  errorTitle: {
    color: colors.foreground,
    fontSize: tokens.fontSize.xlPlus,
    fontWeight: '700',
    textAlign: 'center',
  },
  errorBody: {
    color: colors.text.secondary,
    fontSize: tokens.fontSize.basePlus,
    lineHeight: tokens.fontSize.basePlus * 1.5,
    textAlign: 'center',
  },
  blockedNote: {
    color: colors.status.error.light,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.fontSize.sm * 1.4,
  },
});
